import { ApiService } from "./api";
import type {
  MaarifQuestionAuditPayload,
  MaarifQuestionAuditReport,
  MaarifQuestionAuditResponse,
  MaarifQuestionBankExportInfo,
  MaarifQuestionContextPayload,
  MaarifQuestionContextResponse,
  MaarifQuestionDetectedMetadata,
  MaarifQuestionGenerateFromImagePayload,
  MaarifQuestionGenerateFromImageResponse,
  MaarifQuestionGenerateFromPdfPayload,
  MaarifQuestionPdfJobResponse,
  MaarifQuestionPdfJobStatus,
  MaarifQuestionGenerateResponse,
  MaarifQuestionGeneratePayload,
  MaarifQuestionRecord,
  MaarifQuestionRewritePayload,
  MaarifQuestionRewriteResponse,
  MaarifQuestionSourceAnalysis,
  MaarifSingleQuestion,
  MaarifWorkflowSource,
} from "../types/maarif-question-wizard";

export type MaarifWizardQuestionMode = "context" | "image" | "pdf" | "pdf_contextual";
export type MaarifWizardPanelTab = "workflow" | "settings" | "admin";
export type MaarifWizardAuditMode = "generated" | "external";
export type MaarifWizardTaskType = "context" | "question" | "image" | "pdf" | "audit" | "rewrite";
export type MaarifWizardTaskStatus = "running" | "completed" | "failed";

export interface MaarifWizardFormState {
  title: string;
  lessonId: string;
  gradeLabel: string;
  difficulty: "Kolay" | "Orta" | "Zor";
  lengthPreset: "Kısa" | "Orta" | "Uzun";
  targetWordCount: string;
  wantsVisual: boolean;
  customPrompt: string;
  gainText: string;
  processComponentCode: string;
}

export interface MaarifWizardSnapshot {
  currentStep: number;
  workflowSource: MaarifWorkflowSource;
  questionMode: MaarifWizardQuestionMode;
  panelTab: MaarifWizardPanelTab;
  auditMode: MaarifWizardAuditMode;
  form: MaarifWizardFormState;
  contextText: string;
  contextRationale: string;
  question: MaarifSingleQuestion | null;
  multiQuestions: MaarifSingleQuestion[];
  detectedMetadata: MaarifQuestionDetectedMetadata | null;
  questionSourceAnalysis: MaarifQuestionSourceAnalysis | null;
  pdfFileName: string;
  pdfPageCount: number;
  selectedPdfPages: number[];
  pdfSummary: { questionCount: number; notes?: string } | null;
  pdfJob: MaarifQuestionPdfJobResponse | null;
  externalQuestionText: string;
  auditReport: MaarifQuestionAuditReport | null;
  rewriteQuestion: MaarifSingleQuestion | null;
  questionBankExport: MaarifQuestionBankExportInfo | null;
  modelInfo: {
    contextModel?: string | null;
    questionModel?: string | null;
    questionFromImageModel?: string | null;
    questionsFromPdfModel?: string | null;
    questionImageModel?: string | null;
    auditModel?: string | null;
    rewriteModel?: string | null;
  } | null;
  recordMeta: Pick<MaarifQuestionRecord, "createdAt" | "updatedAt"> | null;
}

export interface MaarifWizardTransientAssets {
  pdfFile: File | null;
  questionImageFile: File | null;
  questionImagePreview: string;
  auditImage: File | null;
  auditImagePreview: string;
}

export interface MaarifWizardPendingTask {
  id: string;
  type: MaarifWizardTaskType;
  status: MaarifWizardTaskStatus;
  result:
    | MaarifQuestionContextResponse
    | MaarifQuestionGenerateResponse
    | MaarifQuestionGenerateFromImageResponse
    | MaarifQuestionPdfJobResponse
    | MaarifQuestionAuditResponse
    | MaarifQuestionRewriteResponse
    | null;
  error: string | null;
  job: MaarifQuestionPdfJobResponse | null;
}

export interface MaarifWizardSessionState {
  recordId: string | null;
  snapshot: MaarifWizardSnapshot;
  transient: MaarifWizardTransientAssets;
  pendingTask: MaarifWizardPendingTask | null;
  restoredOnce: boolean;
  updatedAt: string;
}

interface MaarifWizardTaskController {
  taskId: string;
  isActive: () => boolean;
  updateTask: (updates: Partial<MaarifWizardPendingTask>) => void;
  updateSnapshot: (updates: Partial<MaarifWizardSnapshot>) => void;
}

interface StartTaskOptions<TTaskResult> {
  recordId: string | null;
  snapshot: MaarifWizardSnapshot;
  transient: MaarifWizardTransientAssets;
  taskType: MaarifWizardTaskType;
  runner: (controller: MaarifWizardTaskController) => Promise<TTaskResult>;
}

const MAARIF_WIZARD_SESSION_STORAGE_KEY = "maarifQuestionWizard.session";
const normalizeRecordId = (recordId?: string | null) => recordId || null;
const isSameRoute = (left?: string | null, right?: string | null) => normalizeRecordId(left) === normalizeRecordId(right);
const SESSION_STORAGE_MAX_BYTES = 3_500_000;

const buildEmptyTransientAssets = (): MaarifWizardTransientAssets => ({
  pdfFile: null,
  questionImageFile: null,
  questionImagePreview: "",
  auditImage: null,
  auditImagePreview: "",
});

const isActivePdfJobStatus = (status?: string | null) => status === "queued" || status === "processing";

const stripImageAssetForStorage = (asset: any) => {
  if (!asset || typeof asset !== "object") return asset || null;
  return {
    ...asset,
    base64: "",
  };
};

const stripImageAssetListForStorage = (assets: any) =>
  Array.isArray(assets) ? assets.map((asset) => stripImageAssetForStorage(asset)).filter(Boolean) : assets || null;

const stripRichContentImagesForStorage = (document: any) => {
  if (!document || typeof document !== "object" || !Array.isArray(document.blocks)) return document || null;

  return {
    ...document,
    blocks: document.blocks.map((block: any) =>
      block?.type === "image"
        ? {
            ...block,
            image: stripImageAssetForStorage(block.image),
          }
        : block
    ),
  };
};

const stripQuestionImagesForStorage = (question: any) => {
  if (!question || typeof question !== "object") return question || null;

  const choiceImages = question.choiceImages && typeof question.choiceImages === "object"
    ? Object.fromEntries(
        Object.entries(question.choiceImages).map(([key, value]) => [key, stripImageAssetForStorage(value)])
      )
    : question.choiceImages || null;
  const choiceContents = question.choiceContents && typeof question.choiceContents === "object"
    ? Object.fromEntries(
        Object.entries(question.choiceContents).map(([key, value]) => [key, stripRichContentImagesForStorage(value)])
      )
    : question.choiceContents || null;

  return {
    ...question,
    image: stripImageAssetForStorage(question.image),
    sourceReferenceImage: stripImageAssetForStorage(question.sourceReferenceImage),
    bodyImages: stripImageAssetListForStorage(question.bodyImages),
    images: stripImageAssetListForStorage(question.images),
    supportImages: stripImageAssetListForStorage(question.supportImages),
    questionImages: stripImageAssetListForStorage(question.questionImages),
    questionContent: stripRichContentImagesForStorage(question.questionContent),
    choiceImages,
    choiceContents,
  };
};

const stripPdfJobResultForStorage = (job: MaarifQuestionPdfJobResponse | null | undefined) => {
  if (!job) return null;
  return {
    ...job,
    result: null,
  };
};

const buildPersistableSnapshot = (snapshot: MaarifWizardSnapshot): MaarifWizardSnapshot => ({
  ...snapshot,
  question: stripQuestionImagesForStorage(snapshot.question),
  multiQuestions: Array.isArray(snapshot.multiQuestions)
    ? snapshot.multiQuestions.map(stripQuestionImagesForStorage).filter(Boolean)
    : [],
  rewriteQuestion: stripQuestionImagesForStorage(snapshot.rewriteQuestion),
  pdfJob: stripPdfJobResultForStorage(snapshot.pdfJob),
});

const buildPersistablePendingTask = (pendingTask: MaarifWizardPendingTask | null): MaarifWizardPendingTask | null => {
  if (!pendingTask) return null;

  return {
    ...pendingTask,
    result: null,
    job: stripPdfJobResultForStorage(pendingTask.job),
  };
};

const buildPersistableSessionState = (session: MaarifWizardSessionState) => ({
  recordId: session.recordId,
  snapshot: buildPersistableSnapshot(session.snapshot),
  transient: buildEmptyTransientAssets(),
  pendingTask: buildPersistablePendingTask(session.pendingTask),
  restoredOnce: session.restoredOnce,
  updatedAt: session.updatedAt,
});

const buildResumablePdfPendingTask = (
  snapshot: MaarifWizardSnapshot,
  pendingTask?: MaarifWizardPendingTask | null
): MaarifWizardPendingTask | null => {
  const snapshotPdfJob = snapshot?.pdfJob || null;
  const pendingPdfJob = pendingTask?.type === "pdf" ? pendingTask.job || null : null;
  const activePdfJob = pendingPdfJob || snapshotPdfJob;

  if (!activePdfJob || !isActivePdfJobStatus(activePdfJob.status)) {
    return pendingTask || null;
  }

  if (pendingTask?.type === "pdf" && pendingTask.status === "running") {
    return {
      ...pendingTask,
      job: activePdfJob,
    };
  }

  return {
    id: `restored_pdf_${activePdfJob.id}`,
    type: "pdf",
    status: "running",
    result: null,
    error: null,
    job: activePdfJob,
  };
};

const readStoredSessionState = (): MaarifWizardSessionState | null => {
  if (typeof window === "undefined") return null;

  try {
    const rawValue = window.localStorage.getItem(MAARIF_WIZARD_SESSION_STORAGE_KEY);
    if (!rawValue) return null;

    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object") return null;

    const snapshot = parsed.snapshot;
    const pendingTask = buildResumablePdfPendingTask(snapshot, parsed.pendingTask || null);

    return {
      recordId: normalizeRecordId(parsed.recordId),
      snapshot: {
        ...snapshot,
        pdfJob: pendingTask?.type === "pdf" ? pendingTask.job || snapshot?.pdfJob || null : snapshot?.pdfJob || null,
      },
      transient: buildEmptyTransientAssets(),
      pendingTask,
      restoredOnce: Boolean(parsed.restoredOnce),
      updatedAt: parsed.updatedAt || new Date().toISOString(),
    };
  } catch (error) {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(MAARIF_WIZARD_SESSION_STORAGE_KEY);
    }
    return null;
  }
};

const persistSessionState = (session: MaarifWizardSessionState | null) => {
  if (typeof window === "undefined") return;

  if (!session) {
    window.localStorage.removeItem(MAARIF_WIZARD_SESSION_STORAGE_KEY);
    return;
  }

  const persistableSession = buildPersistableSessionState(session);
  const serialized = JSON.stringify(persistableSession);

  try {
    if (serialized.length <= SESSION_STORAGE_MAX_BYTES) {
      window.localStorage.setItem(MAARIF_WIZARD_SESSION_STORAGE_KEY, serialized);
      return;
    }

    const compactSession = {
      ...persistableSession,
      snapshot: {
        ...persistableSession.snapshot,
        question: null,
        multiQuestions: [],
        rewriteQuestion: null,
        questionSourceAnalysis: null,
        auditReport: null,
        externalQuestionText: "",
      },
    };

    window.localStorage.setItem(MAARIF_WIZARD_SESSION_STORAGE_KEY, JSON.stringify(compactSession));
  } catch (error) {
    window.localStorage.removeItem(MAARIF_WIZARD_SESSION_STORAGE_KEY);
  }
};

let currentSession: MaarifWizardSessionState | null = readStoredSessionState();
let taskCounter = 0;
const sessionListeners = new Set<(session: MaarifWizardSessionState | null) => void>();
let activePersistedPdfResumeTaskId: string | null = null;

const emitSession = () => {
  sessionListeners.forEach((listener) => listener(currentSession));
};

const revokePreviewUrl = (previewUrl?: string | null) => {
  if (previewUrl && previewUrl.startsWith("blob:")) {
    URL.revokeObjectURL(previewUrl);
  }
};

const cleanupTransientAssets = (transient?: Partial<MaarifWizardTransientAssets> | null) => {
  if (!transient) return;
  revokePreviewUrl(transient.questionImagePreview);
  revokePreviewUrl(transient.auditImagePreview);
};

const updateSessionState = (updater: (session: MaarifWizardSessionState | null) => MaarifWizardSessionState | null) => {
  const previousSession = currentSession;
  const nextSession = updater(previousSession);

  if (previousSession && nextSession !== previousSession) {
    const shouldCleanupTransient =
      !nextSession ||
      !isSameRoute(previousSession.recordId, nextSession.recordId) ||
      previousSession.transient.questionImagePreview !== nextSession.transient.questionImagePreview ||
      previousSession.transient.auditImagePreview !== nextSession.transient.auditImagePreview;

    if (shouldCleanupTransient) {
      cleanupTransientAssets(previousSession.transient);
    }
  }

  currentSession = nextSession;
  persistSessionState(currentSession);
  emitSession();
};

const createSessionState = ({
  recordId,
  snapshot,
  transient,
  pendingTask = null,
  restoredOnce = false,
}: {
  recordId: string | null;
  snapshot: MaarifWizardSnapshot;
  transient: MaarifWizardTransientAssets;
  pendingTask?: MaarifWizardPendingTask | null;
  restoredOnce?: boolean;
}): MaarifWizardSessionState => ({
  recordId: normalizeRecordId(recordId),
  snapshot,
  transient,
  pendingTask,
  restoredOnce,
  updatedAt: new Date().toISOString(),
});

const pollMaarifPdfJobUntilComplete = async (
  controller: MaarifWizardTaskController,
  initialJob: MaarifQuestionPdfJobResponse
): Promise<MaarifQuestionPdfJobResponse> => {
  let currentJob = initialJob;

  while (controller.isActive()) {
    controller.updateTask({ job: currentJob });
    controller.updateSnapshot({ pdfJob: currentJob });

    if (currentJob.status === "completed" || currentJob.status === "failed") {
      return currentJob;
    }

    await new Promise((resolve) => {
      setTimeout(resolve, 2000);
    });

    currentJob = await ApiService.getMaarifQuestionsFromPdfJobStatus(currentJob.id);
  }

  throw new Error("PDF işi artık aktif değil.");
};

const shouldResumePersistedPdfTask = (session: MaarifWizardSessionState | null) =>
  Boolean(
    (
      session?.pendingTask?.type === "pdf" &&
      session.pendingTask.status === "running" &&
      (session.pendingTask.job?.id || session.snapshot.pdfJob?.id)
    ) ||
    (
      session?.snapshot?.pdfJob?.id &&
      isActivePdfJobStatus(session.snapshot.pdfJob.status)
    )
  );

const resumePersistedPdfTaskIfNeeded = () => {
  if (!shouldResumePersistedPdfTask(currentSession)) return;

  if (
    currentSession &&
    (!currentSession.pendingTask || currentSession.pendingTask.type !== "pdf" || currentSession.pendingTask.status !== "running")
  ) {
    const resumablePendingTask = buildResumablePdfPendingTask(currentSession.snapshot, currentSession.pendingTask);
    if (resumablePendingTask) {
      currentSession = {
        ...currentSession,
        pendingTask: resumablePendingTask,
        snapshot: {
          ...currentSession.snapshot,
          pdfJob: resumablePendingTask.job || currentSession.snapshot.pdfJob || null,
        },
        updatedAt: new Date().toISOString(),
      };
      persistSessionState(currentSession);
      emitSession();
    }
  }

  const taskId = currentSession?.pendingTask?.id || null;
  if (!taskId || activePersistedPdfResumeTaskId === taskId) return;

  activePersistedPdfResumeTaskId = taskId;

  Promise.resolve()
    .then(async () => {
      while (currentSession?.pendingTask?.id === taskId && currentSession.pendingTask.status === "running") {
        const currentJobId = currentSession.pendingTask.job?.id || currentSession.snapshot.pdfJob?.id;
        if (!currentJobId) {
          throw new Error("PDF iş kimliği bulunamadı.");
        }

        const latestJob = await ApiService.getMaarifQuestionsFromPdfJobStatus(currentJobId);

        updateSessionState((session) => {
          if (!session || session.pendingTask?.id !== taskId || session.pendingTask.type !== "pdf") return session;

          const nextPendingTask: MaarifWizardPendingTask = {
            ...session.pendingTask,
            job: latestJob,
          };

          if (latestJob.status === "completed") {
            nextPendingTask.status = "completed";
            nextPendingTask.result = latestJob;
            nextPendingTask.error = null;
          } else if (latestJob.status === "failed") {
            nextPendingTask.status = "failed";
            nextPendingTask.result = null;
            nextPendingTask.error = latestJob.error || latestJob.message || "PDF işleme tamamlanamadı.";
          }

          return {
            ...session,
            snapshot: {
              ...session.snapshot,
              pdfJob: latestJob,
            },
            pendingTask: nextPendingTask,
            updatedAt: new Date().toISOString(),
          };
        });

        if (latestJob.status === "completed" || latestJob.status === "failed") {
          break;
        }

        await new Promise((resolve) => {
          setTimeout(resolve, 2000);
        });
      }
    })
    .catch((error: any) => {
      updateSessionState((session) => {
        if (!session || session.pendingTask?.id !== taskId || session.pendingTask.type !== "pdf") return session;

        return {
          ...session,
          pendingTask: {
            ...session.pendingTask,
            status: "failed",
            result: null,
            error: error?.message || "PDF işi devam ettirilemedi.",
          },
          updatedAt: new Date().toISOString(),
        };
      });
    })
    .finally(() => {
      if (activePersistedPdfResumeTaskId === taskId) {
        activePersistedPdfResumeTaskId = null;
      }
    });
};

const startBackgroundTask = <TTaskResult>({
  recordId,
  snapshot,
  transient,
  taskType,
  runner,
}: StartTaskOptions<TTaskResult>) => {
  const taskId = `${taskType}_${Date.now()}_${taskCounter += 1}`;

  updateSessionState(() =>
    createSessionState({
      recordId,
      snapshot,
      transient,
      restoredOnce: false,
      pendingTask: {
        id: taskId,
        type: taskType,
        status: "running",
        result: null,
        error: null,
        job: taskType === "pdf" ? snapshot.pdfJob || null : null,
      },
    })
  );

  const controller: MaarifWizardTaskController = {
    taskId,
    isActive: () => Boolean(currentSession?.pendingTask?.id === taskId),
    updateTask: (updates) => {
      updateSessionState((session) => {
        if (!session || session.pendingTask?.id !== taskId) return session;
        return {
          ...session,
          pendingTask: {
            ...session.pendingTask,
            ...updates,
          },
          updatedAt: new Date().toISOString(),
        };
      });
    },
    updateSnapshot: (updates) => {
      updateSessionState((session) => {
        if (!session || session.pendingTask?.id !== taskId) return session;
        return {
          ...session,
          snapshot: {
            ...session.snapshot,
            ...updates,
          },
          updatedAt: new Date().toISOString(),
        };
      });
    },
  };

  Promise.resolve()
    .then(() => runner(controller))
    .then((result) => {
      updateSessionState((session) => {
        if (!session || session.pendingTask?.id !== taskId) return session;
        return {
          ...session,
          pendingTask: {
            ...session.pendingTask,
            status: "completed",
            result: result as MaarifWizardPendingTask["result"],
            error: null,
          },
          updatedAt: new Date().toISOString(),
        };
      });
    })
    .catch((error: any) => {
      updateSessionState((session) => {
        if (!session || session.pendingTask?.id !== taskId) return session;
        return {
          ...session,
          pendingTask: {
            ...session.pendingTask,
            status: "failed",
            error: error?.message || "Beklenmeyen bir hata oluştu.",
          },
          updatedAt: new Date().toISOString(),
        };
      });
    });

  return taskId;
};

export const subscribeMaarifWizardSession = (listener: (session: MaarifWizardSessionState | null) => void) => {
  sessionListeners.add(listener);
  return () => {
    sessionListeners.delete(listener);
  };
};

export const getMaarifWizardSessionForRoute = (recordId?: string | null) => {
  resumePersistedPdfTaskIfNeeded();
  if (!currentSession) return null;
  return isSameRoute(currentSession.recordId, recordId) ? currentSession : null;
};

export const restoreMaarifWizardSession = (recordId?: string | null) => {
  resumePersistedPdfTaskIfNeeded();
  const session = getMaarifWizardSessionForRoute(recordId);
  if (!session) return null;

  updateSessionState((current) => {
    if (!current || !isSameRoute(current.recordId, recordId)) return current;
    return {
      ...current,
      restoredOnce: true,
      updatedAt: new Date().toISOString(),
    };
  });

  return currentSession;
};

export const syncMaarifWizardSessionSnapshot = ({
  recordId,
  snapshot,
  transient,
}: {
  recordId?: string | null;
  snapshot: MaarifWizardSnapshot;
  transient: MaarifWizardTransientAssets;
}) => {
  updateSessionState((session) => {
    if (!session || !isSameRoute(session.recordId, recordId)) return session;

    return {
      ...session,
      snapshot,
      transient,
      updatedAt: new Date().toISOString(),
    };
  });
};

export const persistMaarifWizardSessionOnLeave = ({
  recordId,
  snapshot,
  transient,
  shouldKeep,
}: {
  recordId?: string | null;
  snapshot: MaarifWizardSnapshot;
  transient: MaarifWizardTransientAssets;
  shouldKeep: boolean;
}) => {
  if (!shouldKeep) {
    clearMaarifWizardSession(recordId);
    return false;
  }

  const session = getMaarifWizardSessionForRoute(recordId);
  if (session?.restoredOnce) {
    clearMaarifWizardSession(recordId);
    return false;
  }

  if (!session) {
    updateSessionState(() =>
      createSessionState({
        recordId: normalizeRecordId(recordId),
        snapshot,
        transient,
      })
    );
    return true;
  }

  syncMaarifWizardSessionSnapshot({ recordId, snapshot, transient });
  return true;
};

export const clearMaarifWizardPendingTask = (recordId?: string | null, taskId?: string | null) => {
  updateSessionState((session) => {
    if (!session || !isSameRoute(session.recordId, recordId)) return session;
    if (!taskId || session.pendingTask?.id !== taskId) return session;
    return {
      ...session,
      pendingTask: null,
      updatedAt: new Date().toISOString(),
    };
  });
};

export const clearMaarifWizardSession = (recordId?: string | null) => {
  updateSessionState((session) => {
    if (!session) return null;
    if (recordId !== undefined && !isSameRoute(session.recordId, recordId)) return session;
    return null;
  });
};

export const startMaarifWizardContextTask = (options: {
  recordId?: string | null;
  snapshot: MaarifWizardSnapshot;
  transient: MaarifWizardTransientAssets;
  payload: MaarifQuestionContextPayload;
}) =>
  startBackgroundTask({
    recordId: normalizeRecordId(options.recordId),
    snapshot: options.snapshot,
    transient: options.transient,
    taskType: "context",
    runner: () => ApiService.generateMaarifQuestionContext(options.payload),
  });

export const startMaarifWizardQuestionTask = (options: {
  recordId?: string | null;
  snapshot: MaarifWizardSnapshot;
  transient: MaarifWizardTransientAssets;
  payload: MaarifQuestionGeneratePayload;
}) =>
  startBackgroundTask({
    recordId: normalizeRecordId(options.recordId),
    snapshot: options.snapshot,
    transient: options.transient,
    taskType: "question",
    runner: () => ApiService.generateMaarifSingleQuestion(options.payload),
  });

export const startMaarifWizardImageTask = (options: {
  recordId?: string | null;
  snapshot: MaarifWizardSnapshot;
  transient: MaarifWizardTransientAssets;
  payload: MaarifQuestionGenerateFromImagePayload;
}) =>
  startBackgroundTask({
    recordId: normalizeRecordId(options.recordId),
    snapshot: options.snapshot,
    transient: options.transient,
    taskType: "image",
    runner: () => ApiService.generateMaarifQuestionFromImage(options.payload),
  });

export const startMaarifWizardPdfTask = (options: {
  recordId?: string | null;
  snapshot: MaarifWizardSnapshot;
  transient: MaarifWizardTransientAssets;
  payload: MaarifQuestionGenerateFromPdfPayload;
}) =>
  startBackgroundTask({
    recordId: normalizeRecordId(options.recordId),
    snapshot: options.snapshot,
    transient: options.transient,
    taskType: "pdf",
    runner: async (controller) => {
      const queuedJob = await ApiService.startMaarifQuestionsFromPdfJob(options.payload);
      controller.updateTask({ job: queuedJob });
      controller.updateSnapshot({ pdfJob: queuedJob });

      const finalizedJob = await pollMaarifPdfJobUntilComplete(controller, queuedJob);
      controller.updateTask({ job: finalizedJob });
      controller.updateSnapshot({ pdfJob: finalizedJob });

      if ((finalizedJob.status as MaarifQuestionPdfJobStatus) === "failed") {
        throw new Error(finalizedJob.error || finalizedJob.message || "PDF işleme tamamlanamadı.");
      }

      return finalizedJob;
    },
  });

export const startMaarifWizardAuditTask = (options: {
  recordId?: string | null;
  snapshot: MaarifWizardSnapshot;
  transient: MaarifWizardTransientAssets;
  payload: MaarifQuestionAuditPayload;
}) =>
  startBackgroundTask({
    recordId: normalizeRecordId(options.recordId),
    snapshot: options.snapshot,
    transient: options.transient,
    taskType: "audit",
    runner: () => ApiService.auditMaarifQuestion(options.payload),
  });

export const startMaarifWizardRewriteTask = (options: {
  recordId?: string | null;
  snapshot: MaarifWizardSnapshot;
  transient: MaarifWizardTransientAssets;
  payload: MaarifQuestionRewritePayload;
}) =>
  startBackgroundTask({
    recordId: normalizeRecordId(options.recordId),
    snapshot: options.snapshot,
    transient: options.transient,
    taskType: "rewrite",
    runner: () => ApiService.rewriteMaarifQuestion(options.payload),
  });
